import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import config from '../../config/config';
import { motion, AnimatePresence } from 'framer-motion';
import ParticlesBackground from '../../components/common/ParticlesBackground';
import { 
  FaUser, FaEnvelope, FaLock, FaEye, FaEyeSlash, FaPhone, 
  FaBuilding, FaCheckCircle, FaTimesCircle, FaClock, FaArrowLeft,
  FaArrowRight, FaSpinner, FaPaperPlane, FaUserPlus, FaSearch,
  FaHome, FaTimes, FaSignInAlt
} from 'react-icons/fa';
import '../../styles/register.css';

const RegisterPage = () => {
  const navigate = useNavigate();
  
  // Steps: 1 = Form, 2 = OTP, 3 = Status Tracking
  const [currentStep, setCurrentStep] = useState(1);
  
  // Form Data
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    fullname: '',
    email: '',
    hp: '',
    user_type: 'kl',
    kl: '',
    pd_id: '',
    direktorat: ''
  });
  
  // Options
  const [klOptions, setKlOptions] = useState([]);
  const [pdOptions, setPdOptions] = useState([]);
  
  // OTP
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpTimer, setOtpTimer] = useState(0);
  const [otpFallback, setOtpFallback] = useState(null);
  
  // Status
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [statusEmail, setStatusEmail] = useState('');
  
  // UI States
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [checkEmailInput, setCheckEmailInput] = useState('');
  
  // Fetch KL and PD options
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [klRes, pdRes] = await Promise.all([
          axios.get(`${config.API_URL}/api/register/kl-options`),
          axios.get(`${config.API_URL}/api/register/pd-options`)
        ]);
        
        if (klRes.data.success) setKlOptions(klRes.data.data);
        if (pdRes.data.success) setPdOptions(pdRes.data.data);
      } catch (error) {
        console.error('Error fetching options:', error);
      }
    };
    
    fetchOptions();
  }, []);
  
  // OTP Timer countdown
  useEffect(() => {
    if (otpTimer > 0) {
      const timer = setTimeout(() => setOtpTimer(otpTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpTimer]);
  
  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Reset kl/pd when switching type
      ...(name === 'user_type' && { kl: '', pd_id: '' })
    }));
  };
  
  // Handle OTP input
  const handleOtpChange = (index, value) => {
    if (value.length > 1) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    
    // Auto focus next input
    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  };
  
  // Handle OTP paste
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    const newOtp = pastedData.split('').concat(Array(6).fill('')).slice(0, 6);
    setOtp(newOtp);
  };
  
  // Validate form
  const validateForm = () => {
    if (!formData.username || formData.username.length < 4) {
      setMessage({ type: 'error', text: 'Username minimal 4 karakter' });
      return false;
    }
    if (!formData.password || formData.password.length < 6) {
      setMessage({ type: 'error', text: 'Password minimal 6 karakter' });
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setMessage({ type: 'error', text: 'Password tidak sama' });
      return false;
    }
    if (!formData.fullname) {
      setMessage({ type: 'error', text: 'Nama lengkap wajib diisi' });
      return false;
    }
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setMessage({ type: 'error', text: 'Email tidak valid' });
      return false;
    }
    if (formData.user_type === 'kl' && !formData.kl) {
      setMessage({ type: 'error', text: 'Pilih Kementerian/Lembaga' });
      return false;
    }
    if (formData.user_type === 'pd' && !formData.pd_id) {
      setMessage({ type: 'error', text: 'Pilih Pemerintah Daerah' });
      return false;
    }
    return true;
  };
  
  // Submit registration
  const handleSubmitRegistration = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    setMessage({ type: '', text: '' });
    
    try {
      const res = await axios.post(`${config.API_URL}/api/register`, {
        username: formData.username,
        password: formData.password,
        fullname: formData.fullname,
        email: formData.email,
        hp: formData.hp,
        user_type: formData.user_type,
        kl: formData.kl || null,
        pd_id: formData.pd_id ? parseInt(formData.pd_id) : null,
        direktorat: formData.direktorat
      });
      
      if (res.data.success) {
        setMessage({ type: 'success', text: res.data.message });
        // Check if OTP fallback is provided (email failed)
        if (res.data.data.otp_fallback) {
          setOtpFallback(res.data.data.otp_fallback);
        }
        setCurrentStep(2);
        setOtpTimer(600); // 10 menit
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Gagal mendaftar' 
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Verify OTP
  const handleVerifyOTP = async () => {
    const otpCode = otp.join('');
    
    if (otpCode.length !== 6) {
      setMessage({ type: 'error', text: 'Masukkan 6 digit kode OTP' });
      return;
    }
    
    setLoading(true);
    setMessage({ type: '', text: '' });
    
    try {
      const res = await axios.post(`${config.API_URL}/api/register/verify-otp`, {
        email: formData.email,
        otp: otpCode
      });
      
      if (res.data.success) {
        setMessage({ type: 'success', text: res.data.message });
        setCurrentStep(3);
        checkStatus();
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Kode OTP tidak valid' 
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Resend OTP
  const handleResendOTP = async () => {
    if (otpTimer > 0) return;
    
    setLoading(true);
    
    try {
      const res = await axios.post(`${config.API_URL}/api/register/resend-otp`, {
        email: formData.email
      });
      
      if (res.data.success) {
        setMessage({ type: 'success', text: 'Kode OTP baru telah dikirim' });
        setOtpTimer(600);
        setOtp(['', '', '', '', '', '']);
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Gagal mengirim ulang OTP' 
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Check registration status
  const checkStatus = async (email = formData.email) => {
    if (!email) return;
    
    setLoading(true);
    
    try {
      const res = await axios.get(`${config.API_URL}/api/register/status`, {
        params: { email }
      });
      
      if (res.data.success) {
        setRegistrationStatus(res.data.data);
        setStatusEmail(email);
        setCurrentStep(3);
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Gagal mengecek status' 
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Format timer
  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Get status info
  const getStatusInfo = (status) => {
    switch (status) {
      case 'pending_otp':
        return { 
          icon: <FaEnvelope className="status-icon pending" />, 
          text: 'Menunggu Verifikasi Email',
          color: '#ffc107',
          step: 1
        };
      case 'pending_approval':
        return { 
          icon: <FaClock className="status-icon waiting" />, 
          text: 'Menunggu Persetujuan Admin',
          color: '#17a2b8',
          step: 2
        };
      case 'approved':
        return { 
          icon: <FaCheckCircle className="status-icon success" />, 
          text: 'Disetujui - Anda Dapat Login',
          color: '#28a745',
          step: 3
        };
      case 'rejected':
        return { 
          icon: <FaTimesCircle className="status-icon rejected" />, 
          text: 'Ditolak',
          color: '#dc3545',
          step: 3
        };
      default:
        return { 
          icon: <FaClock className="status-icon" />, 
          text: 'Unknown',
          color: '#6c757d',
          step: 0
        };
    }
  };

  // Render Step 1: Registration Form
  const renderFormStep = () => (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      className="register-form-container"
    >
      <h2 className="register-title">
        <FaUserPlus /> Daftar Akun Baru
      </h2>
      <p className="register-subtitle">Lengkapi data berikut untuk mendaftar</p>
      
      <form onSubmit={handleSubmitRegistration}>
        <div className="form-row">
          <div className="form-group">
            <label><FaUser /> Username</label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              placeholder="Masukkan username"
              required
            />
          </div>
          <div className="form-group">
            <label><FaUser /> Nama Lengkap</label>
            <input
              type="text"
              name="fullname"
              value={formData.fullname}
              onChange={handleInputChange}
              placeholder="Masukkan nama lengkap"
              required
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label><FaEnvelope /> Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="email@domain.com"
              required
            />
          </div>
          <div className="form-group">
            <label><FaPhone /> No. HP (Opsional)</label>
            <input
              type="tel"
              name="hp"
              value={formData.hp}
              onChange={handleInputChange}
              placeholder="08xxxxxxxxxx"
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label><FaLock /> Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Minimal 6 karakter"
                required
              />
              <button 
                type="button" 
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>
          <div className="form-group">
            <label><FaLock /> Konfirmasi Password</label>
            <div className="password-input-wrapper">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Ulangi password"
                required
              />
              <button 
                type="button" 
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>
        </div>
        
        <div className="form-group">
          <label><FaBuilding /> Tipe Instansi</label>
          <div className="radio-group">
            <label className={`radio-option ${formData.user_type === 'kl' ? 'active' : ''}`}>
              <input
                type="radio"
                name="user_type"
                value="kl"
                checked={formData.user_type === 'kl'}
                onChange={handleInputChange}
              />
              <span>Kementerian/Lembaga</span>
            </label>
            <label className={`radio-option ${formData.user_type === 'pd' ? 'active' : ''}`}>
              <input
                type="radio"
                name="user_type"
                value="pd"
                checked={formData.user_type === 'pd'}
                onChange={handleInputChange}
              />
              <span>Pemerintah Daerah</span>
            </label>
          </div>
        </div>
        
        {formData.user_type === 'kl' && (
          <div className="form-row">
            <div className="form-group">
              <label><FaBuilding /> Kementerian/Lembaga</label>
              <select
                name="kl"
                value={formData.kl}
                onChange={handleInputChange}
                required
              >
                <option value="">-- Pilih K/L --</option>
                {klOptions.map(kl => (
                  <option key={kl.kode_kl} value={kl.kode_kl}>
                    {kl.nama_kl}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label><FaBuilding /> Unit/Direktorat (Opsional)</label>
              <input
                type="text"
                name="direktorat"
                value={formData.direktorat}
                onChange={handleInputChange}
                placeholder="Nama unit/direktorat"
              />
            </div>
          </div>
        )}
        
        {formData.user_type === 'pd' && (
          <div className="form-group">
            <label><FaBuilding /> Pemerintah Daerah</label>
            <select
              name="pd_id"
              value={formData.pd_id}
              onChange={handleInputChange}
              required
            >
              <option value="">-- Pilih Pemerintah Daerah --</option>
              {pdOptions.map(pd => (
                <option key={pd.id} value={pd.id}>
                  {pd.pemerintah}
                </option>
              ))}
            </select>
          </div>
        )}
        
        <button type="submit" className="btn-register" disabled={loading}>
          {loading ? (
            <>
              <FaSpinner className="spin" /> Memproses...
            </>
          ) : (
            <>
              Daftar <FaArrowRight />
            </>
          )}
        </button>
      </form>
      
      <div className="register-footer">
        <div className="footer-buttons">
          <button 
            type="button" 
            className="btn-back-home"
            onClick={() => navigate('/')}
          >
            <FaHome /> Kembali ke Beranda
          </button>
          <button 
            type="button" 
            className="btn-check-status"
            onClick={() => setShowStatusModal(true)}
          >
            <FaSearch /> Cek Status Pendaftaran
          </button>
        </div>
        <p className="login-link">
          Sudah punya akun? <Link to="/login">Login di sini</Link>
        </p>
      </div>
    </motion.div>
  );

  // Render Step 2: OTP Verification
  const renderOtpStep = () => (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      className="otp-container"
    >
      <div className="otp-icon">
        <FaEnvelope />
      </div>
      <h2>Verifikasi Email</h2>
      <p>Masukkan kode OTP yang dikirim ke:</p>
      <p className="otp-email">{formData.email}</p>
      
      {/* OTP Fallback display when email fails */}
      {otpFallback && (
        <div className="otp-fallback-box">
          <p>⚠️ Email tidak dapat dikirim. Gunakan kode berikut:</p>
          <div className="otp-fallback-code">{otpFallback}</div>
        </div>
      )}
      
      <div className="otp-inputs" onPaste={handleOtpPaste}>
        {otp.map((digit, index) => (
          <input
            key={index}
            id={`otp-${index}`}
            type="text"
            maxLength="1"
            value={digit}
            onChange={(e) => handleOtpChange(index, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && !digit && index > 0) {
                document.getElementById(`otp-${index - 1}`)?.focus();
              }
            }}
          />
        ))}
      </div>
      
      {otpTimer > 0 && (
        <p className="otp-timer">
          Kode berlaku: <strong>{formatTimer(otpTimer)}</strong>
        </p>
      )}
      
      <button 
        className="btn-verify" 
        onClick={handleVerifyOTP}
        disabled={loading || otp.join('').length !== 6}
      >
        {loading ? (
          <>
            <FaSpinner className="spin" /> Memverifikasi...
          </>
        ) : (
          <>
            <FaCheckCircle /> Verifikasi
          </>
        )}
      </button>
      
      <button 
        className="btn-resend"
        onClick={handleResendOTP}
        disabled={loading || otpTimer > 0}
      >
        {otpTimer > 0 ? (
          `Kirim ulang dalam ${formatTimer(otpTimer)}`
        ) : (
          <>
            <FaPaperPlane /> Kirim Ulang OTP
          </>
        )}
      </button>
      
      <button 
        className="btn-back"
        onClick={() => setCurrentStep(1)}
      >
        <FaArrowLeft /> Kembali
      </button>
    </motion.div>
  );

  // Render Step 3: Status Tracking
  const renderStatusStep = () => {
    const statusInfo = registrationStatus ? getStatusInfo(registrationStatus.status) : null;
    
    return (
      <motion.div
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -50 }}
        className="status-container"
      >
        <h2>Status Pendaftaran</h2>
        
        {registrationStatus && (
          <>
            <div className="status-card">
              <div className="status-header" style={{ borderColor: statusInfo?.color }}>
                {statusInfo?.icon}
                <span style={{ color: statusInfo?.color }}>{statusInfo?.text}</span>
              </div>
              
              <div className="status-details">
                <p><strong>Nama:</strong> {registrationStatus.fullname}</p>
                <p><strong>Email:</strong> {registrationStatus.email}</p>
                <p><strong>Instansi:</strong> {registrationStatus.instansi}</p>
                <p><strong>Tipe:</strong> {registrationStatus.user_type === 'kl' ? 'K/L' : 'Pemerintah Daerah'}</p>
                <p><strong>Terdaftar:</strong> {new Date(registrationStatus.registered_at).toLocaleString('id-ID')}</p>
                {registrationStatus.approved_at && (
                  <p><strong>Diproses:</strong> {new Date(registrationStatus.approved_at).toLocaleString('id-ID')}</p>
                )}
              </div>
              
              {registrationStatus.status === 'rejected' && registrationStatus.rejection_reason && (
                <div className="rejection-reason">
                  <strong>Alasan Penolakan:</strong>
                  <p>{registrationStatus.rejection_reason}</p>
                </div>
              )}
            </div>
            
            {/* Progress Tracker */}
            <div className="progress-tracker">
              <div className={`progress-step ${statusInfo?.step >= 1 ? 'completed' : ''}`}>
                <div className="step-icon">1</div>
                <span>Verifikasi Email</span>
              </div>
              <div className="progress-line"></div>
              <div className={`progress-step ${statusInfo?.step >= 2 ? 'completed' : ''}`}>
                <div className="step-icon">2</div>
                <span>Review Admin</span>
              </div>
              <div className="progress-line"></div>
              <div className={`progress-step ${statusInfo?.step >= 3 && registrationStatus.status === 'approved' ? 'completed' : ''} ${registrationStatus.status === 'rejected' ? 'rejected' : ''}`}>
                <div className="step-icon">3</div>
                <span>Akses Diberikan</span>
              </div>
            </div>
            
            {registrationStatus.status === 'approved' && (
              <Link to="/login" className="btn-login">
                <FaCheckCircle /> Login Sekarang
              </Link>
            )}
          </>
        )}
        
        <div className="status-actions">
          <button 
            className="btn-refresh"
            onClick={() => checkStatus(statusEmail)}
            disabled={loading}
          >
            {loading ? <FaSpinner className="spin" /> : 'Refresh Status'}
          </button>
          
          {registrationStatus?.status === 'approved' ? (
            <Link to="/login" className="btn-go-login">
              <FaSignInAlt /> Coba Login
            </Link>
          ) : (
            <button 
              className="btn-back"
              onClick={() => {
                setCurrentStep(1);
                setRegistrationStatus(null);
              }}
            >
              <FaArrowLeft /> Daftar Akun Baru
            </button>
          )}
          
          <Link to="/" className="btn-back-home-status">
            <FaHome /> Kembali ke Beranda
          </Link>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="register-page">
      <ParticlesBackground />
      
      <div className="register-wrapper">
        {/* Logo and branding */}
        <div className="register-branding">
          <img src="/logo.png" alt="KSP Logo" className="register-logo" />
          <h1>Integrasi IGT</h1>
          <p>Sistem Integrasi Data Informasi Geospasial Tematik</p>
        </div>
        
        {/* Step indicator */}
        <div className="step-indicator">
          <div className={`step ${currentStep >= 1 ? 'active' : ''}`}>
            <span>1</span>
            <p>Isi Form</p>
          </div>
          <div className={`step ${currentStep >= 2 ? 'active' : ''}`}>
            <span>2</span>
            <p>Verifikasi OTP</p>
          </div>
          <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
            <span>3</span>
            <p>Status</p>
          </div>
        </div>
        
        {/* Message */}
        {message.text && (
          <motion.div 
            className={`message ${message.type}`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {message.type === 'error' ? <FaTimesCircle /> : <FaCheckCircle />}
            {message.text}
          </motion.div>
        )}
        
        {/* Content */}
        <AnimatePresence mode="wait">
          {currentStep === 1 && renderFormStep()}
          {currentStep === 2 && renderOtpStep()}
          {currentStep === 3 && renderStatusStep()}
        </AnimatePresence>
      </div>
      
      {/* Status Check Modal */}
      <AnimatePresence>
        {showStatusModal && (
          <motion.div 
            className="status-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowStatusModal(false)}
          >
            <motion.div 
              className="status-modal"
              initial={{ opacity: 0, scale: 0.8, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 50 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button 
                className="modal-close-btn"
                onClick={() => setShowStatusModal(false)}
              >
                <FaTimes />
              </button>
              
              <div className="modal-icon">
                <FaSearch />
              </div>
              
              <h3>Cek Status Pendaftaran</h3>
              <p>Masukkan email yang Anda gunakan saat mendaftar</p>
              
              <div className="modal-input-group">
                <FaEnvelope className="input-icon" />
                <input
                  type="email"
                  value={checkEmailInput}
                  onChange={(e) => setCheckEmailInput(e.target.value)}
                  placeholder="Masukkan email Anda"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && checkEmailInput) {
                      checkStatus(checkEmailInput);
                      setShowStatusModal(false);
                    }
                  }}
                />
              </div>
              
              <div className="modal-buttons">
                <button 
                  className="btn-modal-cancel"
                  onClick={() => setShowStatusModal(false)}
                >
                  Batal
                </button>
                <button 
                  className="btn-modal-submit"
                  onClick={() => {
                    if (checkEmailInput) {
                      checkStatus(checkEmailInput);
                      setShowStatusModal(false);
                    }
                  }}
                  disabled={!checkEmailInput || loading}
                >
                  {loading ? <FaSpinner className="spin" /> : <FaSearch />}
                  Cek Status
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RegisterPage;